import React, { useEffect, useReducer } from "react";
import { Form, Row, Col, Container, FormGroup } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from "../lib/components/LoadingSpinner";
import { Pager } from "../lib/components/Pager";
import {
  CaretUpFill as CaretUp,
  CaretDownFill as CaretDown,
} from "react-bootstrap-icons";
import { withHeaderMessages } from "../higherOrderComponents/WithHeaderMessages";
import { FormattedDate } from "../lib/components/FormattedDate";
import { Currency } from '../lib/components/Currency';
import qs from "qs";

const CommissionsApproval = (props) => {
  const location = useLocation();
  const { searchTerm, page, pageSize } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const navigate = useNavigate();

  const initialState = {
    searchTerm: searchTerm || "",
    page: page * 1 || 1,
    pageSize: pageSize * 1 || 10,
    results: [],
    maxItems: 0,
    maxPages: 1,
    isLoading: false,
    orderBy: "contractnumber",
    orderByDesc: false,
    selectedContracts: [],
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "searchTerm":
        return {
          ...state,
          searchTerm: action.payload,
        };
      case "page":
        return {
          ...state,
          page: action.payload,
        };
      case "pageSize":
        return {
          ...state,
          pageSize: action.payload,
        };
      case "results":
        return {
          ...state,
          results: action.payload,
        };
      case "maxItems":
        return {
          ...state,
          maxItems: action.payload,
        };
      case "maxPages":
        return {
          ...state,
          maxPages: action.payload,
        };
      case "orderBy":
        return {
          ...state,
          orderBy: action.payload,
        };
      case "orderByDesc":
        return {
          ...state,
          orderByDesc: action.payload,
        };
      case "selectedContracts":
        return {
          ...state,
          selectedContracts: action.payload,
        };
      case "isLoading":
        return {
          ...state,
          isLoading: action.payload,
        };
      case "hideLoader":
        return {
          ...state,
          isLoading: false,
        };
      default:
        throw new Error();
    }
  };

  const [contractSearchState, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const { searchTerm, page, pageSize } = contractSearchState;
    props.header.setHeader("contractSearch");
    search(searchTerm, page, pageSize);
  }, [searchTerm]);

  const handleSearchChanged = (event) => {
    dispatch({ type: "searchTerm", payload: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const { searchTerm, pageSize } = contractSearchState;
    dispatch({ type: "page", payload: 1 });
    searchAndSetHistory(searchTerm, 1, pageSize);
  };

  const pagingChangeHandler = (page, pageSize) => {
    const { searchTerm } = contractSearchState;

    dispatch({ type: "page", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    searchAndSetHistory(searchTerm, page, pageSize);
  };

  const searchAndSetHistory = (searchTerm, page, pageSize) => {
    navigate(
      location.pathname +
        qs.stringify(
          { searchTerm: searchTerm, page: page, pageSize: pageSize },
          { addQueryPrefix: true },
        ),
    );
    const { orderBy, orderByDesc } = contractSearchState;

    search(searchTerm, page, pageSize, orderBy, orderByDesc);
  };

  const search = async (searchTerm, page, pageSize, orderBy, orderByDesc) => {
    let search = {
      Query: searchTerm,
      Options: {
        Page: page,
        ResultsPerPage: pageSize,
        Sort: {
          Name: orderBy,
          Descending: orderByDesc,
        },
      },
    };

    let searchQueryString = qs.stringify(search, {
      allowDots: true,
      encode: false,
      addQueryPrefix: true,
    });
    dispatch({ type: "isLoading", payload: true });

    await props.api
      .get("/api/contracts/search" + searchQueryString)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
      })
      .then((data) => {
        dispatch({
          type: "results",
          payload: data.results,
          maxItems: data.totalRecords,
          maxPages: data.totalPages,
        });
        dispatch({
          type: "maxItems",
          payload: data.totalRecords,
          maxPages: data.totalPages,
        });
        dispatch({ type: "maxPages", payload: data.totalPages });
        dispatch({ type: "isLoading", payload: false });
      })
      .catch();
  };

  const handleSortResults = (newOrderBy) => {
    const { searchTerm, page, pageSize, orderBy, orderByDesc } =
      contractSearchState;
    const newOrderByDesc = orderBy === newOrderBy ? !orderByDesc : false;
    dispatch({ type: "orderBy", payload: newOrderBy });
    dispatch({ type: "orderByDesc", payload: newOrderByDesc });
    search(searchTerm, page, pageSize, newOrderBy, newOrderByDesc);
  };

  const viewContractsIsDisabled = () => {
    return contractSearchState.selectedContracts.length === 0;
  };

  const viewContracts = () => {
    navigate(
      "/contracts-combined?n=" +
        contractSearchState.selectedContracts.join(","),
    );
  };

  const handleContractSelected = (contract, event) => {
    const { selectedContracts } = contractSearchState;
    const index = selectedContracts.indexOf(contract.number);
    if (index > -1) {
      selectedContracts.splice(index, 1);
    } else {
      selectedContracts.splice(0, 0, contract.number);
    }
    dispatch({ type: "selectedContracts", payload: selectedContracts });
  };

  const order = `${contractSearchState.orderBy}_${contractSearchState.orderByDesc ? "desc" : "asc"}`;
  return (
    <Container>
      <Col sm={4} className="text-start me-auto mt-2 mb-3">
        <Form className="input-group section ">
          <input
            type="text"
            name="searchCriteria"
            onChange={handleSearchChanged}
            className="form-control"
            placeholder="Enter Name or Contract #"
          />
          <span className="input-group-btn">
            <button
              className="btn btn-default btn-sec"
              onClick={(event) => handleSubmit(event)}
              disabled={contractSearchState.isLoading}
            >
              Search
            </button>
          </span>
        </Form>
      </Col>
      {contractSearchState.isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Row className="section">
            <Col md={12}>
              <div className="scroll-table">
                <table responsive className="table">
                  <thead>
                    <tr>
                      <th></th>
                      <th
                        className="sortable-header"
                        onClick={(event) =>
                          handleSortResults("providercontractnumber")
                        }
                      >
                        <div className="sortable-header-text">Provider #</div>
                        <div>
                          <span
                            style={{
                              opacity:
                                order === "providercontractnumber_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity:
                                order === "providercontractnumber_desc"
                                  ? 1
                                  : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("contractnumber")}
                      >
                        <div className="sortable-header-text">ClearPoint #</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "contractnumber_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "contractnumber_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("provider")}
                      >
                        <div className="sortable-header-text">Provider</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "provider_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "provider_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("agent")}
                      >
                        <div className="sortable-header-text">Agent</div>
                        <div>
                          <span
                            style={{ opacity: order === "agent_asc" ? 1 : "" }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{ opacity: order === "agent_desc" ? 1 : "" }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("recipient")}
                      >
                        <div className="sortable-header-text">Recipients</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "recipient_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "recipient_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("purchaser")}
                      >
                        <div className="sortable-header-text">Purchaser</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "purchaser_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "purchaser_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("status")}
                      >
                        <div className="sortable-header-text">Status</div>
                        <div>
                          <span
                            style={{ opacity: order === "status_asc" ? 1 : "" }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "status_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("accepted")}
                      >
                        <div className="sortable-header-text">
                          Accepted Date
                        </div>
                        <div>
                          <span
                            style={{
                              opacity: order === "accepted_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "accepted_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={(event) => handleSortResults("netprice")}
                      >
                        <div className="sortable-header-text">
                          Plan Amount
                        </div>
                        <div>
                          <span
                            style={{
                              opacity: order === "netprice_asc" ? 1 : "",
                            }}
                          >
                            {<CaretUp />}
                          </span>
                          <span
                            style={{
                              opacity: order === "netprice_desc" ? 1 : "",
                            }}
                          >
                            {<CaretDown />}
                          </span>
                        </div>
                      </th>
                      <th>&nbsp;</th>
                      <th>Approve</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(contractSearchState?.results || []).map((r) => (
                      <tr
                        key={r.number}
                        className={r.hasNotes ? "color-red" : ""}
                      >
                        <td className="text-center">
                        </td>
                        <td>{r.providerNumber}</td>
                        <td>{r.number}</td>
                        <td>{r.provider ? r.provider.name : ""}</td>
                        <td>{`${r.agent ? r.agent.firstName : ""} ${r.agent ? r.agent.lastName : ""}`}</td>
                        <td>
                          {r.recipients
                            .map((rec) => `${rec.firstName} ${rec.lastName}`)
                            .join(", ")}
                        </td>
                        <td>{`${r.purchaser.firstName} ${r.purchaser.lastName}`}</td>
                        <td>{r.status}</td>
                        <td>
                          {r.accepted ? (
                            <FormattedDate date={r.accepted} />
                          ) : (
                            ""
                          )}
                        </td>
                        <td><Currency amount={r.netPrice} /></td>
                        <td>
                          <Link
                            to={`/contracts/${r.number}`}
                            className="contractDetails"
                          >
                            View Details
                          </Link>
                        </td>
                        <td className="text-center">
                          <FormGroup controlId="formBasicCheckbox">
                            <Form.Check
                              name={r.number}
                              type="checkbox"
                              onChange={(event) =>
                                handleContractSelected(r, event)
                              }
                              style={{
                                padding: "0",
                                margin: "0",
                                minHeight: "15px",
                              }}
                            ></Form.Check>
                          </FormGroup></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Col className="text-end">
                  <button
                    className="btn btn-sec"
                    disabled={viewContractsIsDisabled()}
                    onClick={viewContracts}
                  >
                    Approve
                  </button>
                </Col>
              </div>
            </Col>
          </Row>
          <Pager
            maxPages={contractSearchState.maxPages}
            maxItems={contractSearchState.maxItems}
            pageSize={contractSearchState.pageSize}
            page={contractSearchState.page}
            onPageChange={pagingChangeHandler}
            onPageSizeChange={pagingChangeHandler}
          />
        </>
      )}
    </Container>
  );
};

export default withHeaderMessages(WithApiAccess(CommissionsApproval));
